import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Badge,
  theme,
  Typography,
  Button,
  notification as antNotification,
} from 'antd'
import type { MenuProps } from 'antd'
import {
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MessageOutlined,
  SunOutlined,
  MoonOutlined,
  ApiOutlined,
} from '@ant-design/icons'
import { useAuth } from '@/contexts/AuthContext'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { usePermissions } from '@/hooks/usePermissions'
import { useTheme } from '@/contexts/ThemeContext'
import axiosInstance from '@/lib/axios'
import { useRegistryMenu } from '@/hooks/useRegistryMenu'

const { Header, Sider, Content } = Layout
const { Text } = Typography

// ─── Badge overlay post-generazione ───────────────────────────────────────────
//
// useRegistryMenu costruisce le voci dal registro ma non conosce i badge
// dinamici (contatori live via polling). Questa funzione applica i badge
// alle 3 voci che li richiedono DOPO la generazione.
//
// I contatori arrivano già gated per permesso dal chiamante:
//   alertCountEffective = canViewComunicazioni ? alertCount : 0
//   mancaRdoCount       = hasPermission('rdos.view') ? count : 0

type AntMenuItem = NonNullable<NonNullable<MenuProps['items']>[number]>

interface BadgeState {
  pendingCount: number
  alertCountEffective: number
  interniCount: number
  mancaRdoCount: number
}

function applyMenuBadges(
  items: NonNullable<MenuProps['items']>,
  s: BadgeState,
): NonNullable<MenuProps['items']> {
  return items.map(item => {
    if (!item || !('key' in item)) return item
    const key = (item as AntMenuItem & { key?: unknown }).key
    if (key === '/users') {
      return {
        ...item,
        icon: (
          <Badge count={s.pendingCount} size="small" offset={[4, 0]}>
            <TeamOutlined />
          </Badge>
        ),
      } as AntMenuItem
    }
    if (key === '/comunicazioni') {
      return {
        ...item,
        icon: (
          <Badge
            count={s.alertCountEffective + s.interniCount}
            size="small"
            offset={[4, 0]}
          >
            <MessageOutlined />
          </Badge>
        ),
      } as AntMenuItem
    }
    if (key === '/rda') {
      return {
        ...item,
        label: (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {s.mancaRdoCount > 0 && <Badge count={s.mancaRdoCount} size="small" />}
            RDO (Offerte)
          </span>
        ),
      } as AntMenuItem
    }
    return item
  })
}

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const [alertCount, setAlertCount] = useState(0)
  const [interniCount, setInterniCount] = useState(0)
  const [pendingCount, setPendingCount] = useState(0)
  const [mancaRdoCount, setMancaRdoCount] = useState(0)
  const prevCountRef = useRef(-1)
  const [api, contextHolder] = antNotification.useNotification()
  const navigate = useNavigate()
  const { title } = usePageTitle()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { token } = theme.useToken()
  const { hasPermission } = usePermissions()
  const { isDark, toggleTheme } = useTheme()

  const handleLogout = useCallback(() => {
    logout()
  }, [logout])

  // Solo le voci-rotta (key che inizia con '/') causano navigazione.
  // Le voci gruppo (key = 'gruppo-*') aprono/chiudono il sottomenu via Ant Design.
  const handleMenuClick = useCallback(({ key }: { key: string }) => {
    if (key.startsWith('/')) navigate(key)
  }, [navigate])

  const canViewComunicazioni = hasPermission('comunicazioni.view')

  // Chiavi gruppo = 'gruppo-{entry.gruppo}' come prodotte da useRegistryMenu.
  const anagraficheRoutes = ['/customers', '/vehicles', '/suppliers', '/officine', '/rdo-templates']
  const adcRoutes = ['/courtesy-cars', '/courtesy-cars/planning', '/courtesy-cars/consegna']
  const gommeRoutes = ['/tire-service', '/tire-checkin']
  useEffect(() => {
    if (anagraficheRoutes.includes(location.pathname)) {
      setOpenKeys(['gruppo-Anagrafiche'])
    } else if (adcRoutes.includes(location.pathname)) {
      setOpenKeys(['gruppo-Auto di Cortesia'])
    } else if (gommeRoutes.includes(location.pathname)) {
      setOpenKeys(['gruppo-Servizio Gomme'])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (!canViewComunicazioni) return

    const fetchAlerts = async () => {
      try {
        const res = await axiosInstance.get('/comunicazioni/alerts')
        const newCount: number = res.data.count ?? 0
        setAlertCount(newCount)

        // Show toast if new messages arrived (skip on first load)
        if (newCount > prevCountRef.current && prevCountRef.current !== -1) {
          const items = res.data.items ?? []
          const latest = items[0]
          api.info({
            message: 'Nuovo messaggio ricevuto',
            description: latest
              ? `Da: ${latest.destinatario_nome ?? latest.destinatario_numero ?? 'Sconosciuto'} — ${latest.testo?.substring(0, 80) ?? ''}`
              : `${newCount} nuovi messaggi in arrivo`,
            placement: 'bottomRight',
            duration: 6,
            onClick: () => navigate('/comunicazioni'),
          })
        }
        prevCountRef.current = newCount
      } catch {
        // silent fail
      }
    }

    fetchAlerts() // call immediately
    const interval = setInterval(fetchAlerts, 30000)
    return () => clearInterval(interval)
  }, [canViewComunicazioni, api, navigate])

  useEffect(() => {
    const fetchInterni = async () => {
      try {
        const res = await axiosInstance.get('/messaggi-interni/alerts')
        setInterniCount(res.data.non_letti ?? 0)
      } catch { /* silent */ }
    }
    fetchInterni()
    const interval = setInterval(fetchInterni, 30000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!hasPermission('rdos.view')) return
    const fetchMancaRdo = async () => {
      try {
        const res = await axiosInstance.get('/purchase-requests/manca-rdo-count')
        setMancaRdoCount(res.data.count ?? 0)
      } catch { /* silent */ }
    }
    fetchMancaRdo()
    const interval = setInterval(fetchMancaRdo, 30000)
    return () => clearInterval(interval)
  }, [hasPermission])

  useEffect(() => {
    if (!hasPermission('system.manage_users')) return
    axiosInstance.get('/users/')
      .then((res) => {
        const all = res.data as any[]
        setPendingCount(all.filter((u) => u.attivo === false).length)
      })
      .catch(() => { /* silent */ })
  }, [hasPermission])

  // Menu generato dal registro — badge dinamici applicati post-generazione.
  const { items: baseMenuItems } = useRegistryMenu({ excludeGruppi: ['menu utente'] })

  const menuItems = useMemo<MenuProps['items']>(() => {
    return applyMenuBadges(baseMenuItems ?? [], {
      pendingCount,
      alertCountEffective: canViewComunicazioni ? alertCount : 0,
      interniCount,
      mancaRdoCount: hasPermission('rdos.view') ? mancaRdoCount : 0,
    })
  }, [baseMenuItems, pendingCount, alertCount, interniCount, canViewComunicazioni, mancaRdoCount, hasPermission])

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profilo',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    ...(hasPermission('system.settings') ? [
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: 'Impostazioni',
        onClick: () => navigate('/settings'),
      },
      {
        key: 'integrazioni',
        icon: <ApiOutlined />,
        label: 'Integrazioni',
        onClick: () => navigate('/integrazioni'),
      },
    ] : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {contextHolder}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="light"
        style={{
          borderRight: `1px solid ${token.colorBorder}`,
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: `1px solid ${token.colorBorder}`,
            padding: collapsed ? '0 8px' : '0 16px',
            overflow: 'hidden',
          }}
        >
          <img
            src="/Tiber-Garage.png"
            alt="Garage"
            style={{
              height: collapsed ? 48 : '100%',
              maxHeight: collapsed ? 48 : 56,
              width: 'auto',
              objectFit: 'contain',
              transition: 'opacity 0.2s ease',
            }}
          />
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems ?? []}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            padding: '0 24px',
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorder}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          {/* Left: Menu Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 18 }}
              title={collapsed ? 'Apri menù' : 'Chiudi menù'}
              aria-label={collapsed ? 'Apri menù' : 'Chiudi menù'}
              aria-expanded={!collapsed}
            />

            {/* Page Title */}
            {title && (
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: token.colorTextHeading,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Text>
            )}
          </div>

          {/* Right: Icons and User Menu */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            {/* Notifiche */}
            <Badge count={0} showZero={false}>
              <Button
                type="text"
                icon={<BellOutlined style={{ fontSize: 20 }} />}
                onClick={() => navigate('/notifications')}
                aria-label="Notifiche"
                title="Notifiche"
              />
            </Badge>

            {/* Toggle tema chiaro/scuro */}
            <Button
              type="text"
              icon={isDark ? <SunOutlined style={{ fontSize: 18 }} /> : <MoonOutlined style={{ fontSize: 18 }} />}
              onClick={toggleTheme}
              aria-label={isDark ? 'Passa a tema chiaro' : 'Passa a tema scuro'}
              title={isDark ? 'Tema chiaro' : 'Tema scuro'}
            />

            {/* Impostazioni (ADMIN e GM) */}
            {hasPermission('system.settings') && (
              <Button
                type="text"
                icon={<SettingOutlined style={{ fontSize: 20 }} />}
                onClick={() => navigate('/settings')}
                aria-label="Impostazioni Sistema"
                title="Impostazioni Sistema"
                style={{ color: token.colorPrimary }}
              />
            )}

            {/* Menu utente */}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <button
                type="button"
                aria-label={`Menu utente: ${user?.nome ?? ''} ${user?.cognome ?? ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: token.borderRadius,
                  gap: 8,
                }}
              >
                <Avatar icon={<UserOutlined />} />
                <Text>{user?.nome} {user?.cognome}</Text>
              </button>
            </Dropdown>

          </div>
        </Header>

        <Content
          style={{
            margin: 24,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout
